import { useState } from 'react';
import { toast } from 'sonner';
import type { DocumentRenderTemplate } from '@vivport/backend/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { assertToolSucceeded } from '@/api/templates-api';
import {
  useAddTemplateSection,
  useExcludeTemplateBlocks,
  useRestoreTemplateBlocks,
} from '@/hooks/useTemplateQueries';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Notice } from '@/components/ui/notice';

interface TemplateDraftBannerProps {
  template: DocumentRenderTemplate;
  definitionRootId: number;
}

function surfaceError(error: unknown): void {
  toast.error(error instanceof Error ? error.message : 'The action failed.');
}

// Coverage and exclusions are the backend's facts (template.draft); the banner only renders
// them and calls the same tools Neo uses to change them.
export function TemplateDraftBanner({ template, definitionRootId }: TemplateDraftBannerProps) {
  const draft = template.draft;
  const [excludedOpen, setExcludedOpen] = useState(false);
  const addSection = useAddTemplateSection();
  const excludeBlocks = useExcludeTemplateBlocks();
  const restoreBlocks = useRestoreTemplateBlocks();
  const busy = addSection.isPending || excludeBlocks.isPending || restoreBlocks.isPending;

  if (draft === undefined) return null;

  const handleAddAsSection = async (blockId: number, preview: string) => {
    const title = window.prompt('Section title', preview.slice(0, 60) || `Block ${blockId}`);
    if (title === null || title.trim().length === 0) return;
    try {
      assertToolSucceeded(
        await addSection.mutateAsync({
          definitionDocumentId: definitionRootId,
          title: title.trim(),
          guidance: 'Added from the source outline.',
          blockIds: [blockId],
        })
      );
    } catch (error) {
      surfaceError(error);
    }
  };

  const handleExclude = async (blockId: number) => {
    const reason = window.prompt('Why is this block excluded?');
    if (reason === null || reason.trim().length === 0) return;
    try {
      assertToolSucceeded(
        await excludeBlocks.mutateAsync({
          templateId: template.templateContentId,
          blockIds: [blockId],
          reason: reason.trim(),
        })
      );
    } catch (error) {
      surfaceError(error);
    }
  };

  const handleRestore = async (blockIds: number[]) => {
    try {
      assertToolSucceeded(
        await restoreBlocks.mutateAsync({ templateId: template.templateContentId, blockIds })
      );
    } catch (error) {
      surfaceError(error);
    }
  };

  const excludedCount = draft.excluded.reduce((sum, entry) => sum + entry.ids.length, 0);

  return (
    <div data-testid="template-draft-banner" className="mb-4 space-y-3">
      {draft.uncoveredBlocks.length > 0 ? (
        <Notice tone="warning" className="p-3">
          <p className="mb-2 font-medium">
            {draft.uncoveredBlocks.length} source block(s) are not yet covered by a section or an
            exclusion — publish is blocked until each is placed.
          </p>
          <ul className="space-y-2">
            {draft.uncoveredBlocks.map((block) => (
              <li key={block.id} className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">
                  {block.kind} #{block.id}
                </Badge>
                <span className="min-w-0 flex-1 truncate text-muted-foreground">{block.preview}</span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void handleAddAsSection(block.id, block.preview)}
                >
                  Add as section
                </Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => void handleExclude(block.id)}>
                  Exclude…
                </Button>
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}
      {excludedCount > 0 ? (
        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <button
            type="button"
            className="flex items-center gap-1 font-medium"
            onClick={() => setExcludedOpen((open) => !open)}
          >
            {excludedOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Excluded from the source ({excludedCount} block(s))
          </button>
          {excludedOpen ? (
            <ul className="mt-2 space-y-2">
              {draft.excluded.map((entry, index) => (
                <li key={index} className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">blocks {entry.ids.join(', ')}</Badge>
                  <span className="min-w-0 flex-1 truncate text-muted-foreground">{entry.reason}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => void handleRestore(entry.ids)}
                  >
                    Restore
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
